import React from 'react';
import { Link } from 'react-router-dom';
import { Eye, Package, ThumbsUp, Images, Gavel, Layers } from 'lucide-react';

const TYPE_CONFIG = {
  auction: { label: 'AUCTION', icon: Gavel, cls: 'bg-amber-500/20 border-amber-500/40 text-amber-300' },
  pack:    { label: 'PACK',    icon: Layers, cls: 'bg-blue-500/20 border-blue-500/40 text-blue-300' },
};

export default function ListingCard({ listing }) {
  const {
    id, slug, title, game_name, price, quantity, image_url, image_urls = [],
    seller_username, seller_rating = 0, contact_reveal_count = 0,
    listing_type = 'retail', current_bid, min_bid, instant_sell_price, min_quantity,
  } = listing;

  const primaryImage = image_urls[0] || image_url;
  const listingUrl = `/listings/${slug || id}`;
  const isAvailable = quantity > 0;
  const typeConf = TYPE_CONFIG[listing_type];
  const TypeIcon = typeConf?.icon;

  const displayPrice = listing_type === 'auction'
    ? (current_bid ? `$${current_bid} bid` : (min_bid ? `$${min_bid} start` : `$${price}`))
    : `$${price}`;

  return (
    <Link to={listingUrl} data-testid={`listing-card-${id}`}
      className={`block bg-rm-card rounded-sm overflow-hidden card-hover card-shine group animate-fade-in ${
        listing_type === 'auction'
          ? 'border border-amber-500/20 hover:border-amber-500/40'
          : listing_type === 'pack'
          ? 'border border-blue-500/20 hover:border-blue-500/40'
          : 'border border-[#1e1e1e]'
      }`}>
      {/* Image */}
      <div className="relative aspect-[4/3] bg-rm-surface overflow-hidden">
        {primaryImage ? (
          <img src={primaryImage} alt={title}
            className="w-full h-full object-cover group-hover:scale-[1.06] transition-transform duration-700 ease-out" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-[#111]">
            <Package size={36} className="text-zinc-700" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        {/* Top badges */}
        <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5 flex-wrap">
          <span className="bg-black/75 backdrop-blur-sm border border-white/10 px-2 py-0.5 text-[9px] font-rajdhani font-bold uppercase tracking-[0.12em] text-zinc-400">
            {game_name}
          </span>
          {typeConf && (
            <span className={`flex items-center gap-1 backdrop-blur-sm border px-2 py-0.5 text-[9px] font-rajdhani font-bold uppercase tracking-[0.1em] ${typeConf.cls}`}>
              {TypeIcon && <TypeIcon size={8} />} {typeConf.label}
            </span>
          )}
        </div>

        {image_urls.length > 1 && (
          <div className="absolute top-2.5 right-2.5 bg-black/75 backdrop-blur-sm border border-white/10 px-1.5 py-0.5 rounded-sm text-[9px] text-zinc-400 flex items-center gap-1">
            <Images size={9} /> {image_urls.length}
          </div>
        )}

        {!isAvailable && (
          <div className="absolute inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center">
            <span className="font-rajdhani font-bold text-rm-red uppercase tracking-[0.2em] text-sm border border-rm-red/40 px-3 py-1">
              SOLD OUT
            </span>
          </div>
        )}

        <div className="absolute bottom-2.5 right-2.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <span className={`font-rajdhani font-bold text-white text-sm px-2.5 py-1 shadow-lg ${
            listing_type === 'auction' ? 'bg-amber-600' : listing_type === 'pack' ? 'bg-blue-600' : 'bg-rm-red'
          }`}>
            {displayPrice}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-rajdhani font-bold text-[0.95rem] text-white uppercase tracking-wide truncate mb-3 group-hover:text-rm-red transition-colors duration-200">
          {title}
        </h3>

        <div className="flex items-center justify-between mb-3.5">
          <span className={`price-badge text-2xl ${listing_type === 'auction' ? 'text-amber-400' : listing_type === 'pack' ? 'text-blue-400' : ''}`}>
            {displayPrice}
          </span>
          <div className="flex flex-col items-end gap-1">
            {listing_type === 'pack' && min_quantity && (
              <span className="text-[10px] text-blue-400 font-medium">Min qty: {min_quantity}</span>
            )}
            {listing_type === 'auction' && instant_sell_price && (
              <span className="text-[10px] text-green-400 font-medium">Buy now: ${instant_sell_price}</span>
            )}
            <span className={`text-xs font-medium px-2 py-0.5 rounded-sm border ${
              isAvailable ? 'border-green-500/20 text-green-500 bg-green-500/5' : 'border-rm-red/20 text-rm-red/70 bg-rm-red/5'
            }`}>
              {isAvailable ? `Qty: ${quantity}` : 'Sold Out'}
            </span>
          </div>
        </div>

        <div className="border-t border-[#1e1e1e] pt-3 flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-[10px] text-zinc-600 uppercase tracking-widest mb-0.5">Seller</p>
            <p className="text-xs font-medium text-zinc-300 truncate max-w-[110px]">{seller_username}</p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <div className="flex items-center gap-1.5">
              <ThumbsUp size={10} className="text-green-500" />
              <span className="text-[11px] text-green-500 font-semibold">{seller_rating}%</span>
            </div>
            <div className="flex items-center gap-1.5 text-zinc-600">
              <Eye size={10} />
              <span className="text-[10px]">{contact_reveal_count}</span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
